/** The workshop's figures. The model's come from /workshop's `model` (backend
 *  utils/workshopModel.js), for the charged window shown; every other figure is
 *  workshop.json's, illustrative. */

const pct = (a, b) => (a ? ((b - a) / a) * 100 : null);
const int = (n) => Math.round(n).toLocaleString('en-US');
const share = (x) => `${(x * 100).toFixed(1)}%`;
const hourText = (h) => {
  const n = Number(h);
  if (n === 0) return '12 am';
  if (n === 12) return '12 pm';
  return n > 12 ? `${n - 12} pm` : `${n} am`;
};

/** The model's charged windows, or null for a metro whose pack has no pricing scenarios. */
export const periodsOf = (workshop) => (workshop.model?.windows?.length ? workshop.model.windows : null);

/** One scenario's figures in one window, or null when the model does not hold the scenario. */
export const windowOf = (workshop, scenarioId, period) =>
  (period && workshop.model?.scenarios?.[scenarioId]?.windows?.[period.id]) || null;

/** Why a scenario (or the run a map shows for it) has no figures in the window, or null. */
export function reasonFor(workshop, scenarioId, period) {
  const w = windowOf(workshop, scenarioId, period);
  return w && !w.computed ? w.reason : null;
}

/** Why a map has no run to draw for `run` in the window: the model's reason, or a
 *  pricing run the metro's pack does not hold yet; null when it has one. */
export function runNotice(workshop, run, period) {
  if (!run || run === 'baseline') return null;
  const scenarios = workshop.model?.scenarios;
  if (scenarios && Object.keys(scenarios).length && !scenarios[run]) {
    return 'This scenario is not among the pre-computed results yet.';
  }
  return reasonFor(workshop, run, period);
}

const perHour = (w) => w.byHour && Object.entries(w.byHour)
  .map(([h, r]) => `${hourText(h)}–${hourText(Number(h) + 1)}: ${int(r.entering.baseline)} → ${int(r.entering.scenario)} vehicles entering, $${int(r.paidUsd)} in charges`)
  .join('\n');

/** The pricing scenario whose drivers' routes weigh neighborhood bands (its model entry
 *  has `routeCost`), or null. */
export const routeCostId = (workshop) => Object.keys(workshop.model?.scenarios || {})
  .find((id) => workshop.model.scenarios[id].routeCost) || null;

/** Its corridor figures in the window: per band and group, vehicles for the baseline, the
 *  scenario it is compared against and itself. Null when it has none there. */
export function corridorsIn(workshop, period) {
  const id = routeCostId(workshop);
  const w = id ? windowOf(workshop, id, period) : null;
  return w && w.computed && w.routeCost ? w.routeCost : null;
}

/** Rows the comparison adds while the model has corridor figures: vehicles on the bands'
 *  treated streets and on the residential streets beside them, all bands together and each
 *  vehicle once. Only the columns the bake counts (no charge, scenario 3, scenario 4) have
 *  a value. */
export const CORRIDOR_ROWS = [
  { id: 'band_vehicles', group: 'treated_edges', label: 'Vehicles on the protected bands', unit: 'pct', better: 'lower' },
  { id: 'parallel_vehicles', group: 'parallel_edges', label: 'Vehicles on residential streets beside them', unit: 'pct', better: 'lower' },
];

function corridorFigure(workshop, period, scenario, row) {
  const rc = corridorsIn(workshop, period);
  const vehicles = rc && rc[row.group] ? rc[row.group].vehicles : null;
  if (!vehicles || vehicles.baseline == null) return { kind: 'empty' };
  const base = vehicles.baseline;
  if (scenario.id === 'baseline') return { kind: 'baseline', value: 0, detail: `${int(base)} vehicles` };
  const v = vehicles[scenario.id];
  if (v == null) return { kind: 'empty', title: 'Counted for no charge and the scenarios the bands are judged by only' };
  return { kind: 'model', value: pct(base, v), detail: `${int(base)} → ${int(v)} vehicles` };
}

/** One figure of a scenario: { kind: 'model' | 'baseline' | 'illustrative' | 'empty',
 *  value, detail, title }. A scenario with no figures in the window is the caller's to
 *  show (reasonFor); here it falls back to workshop.json. */
/** Headline metrics the Analytics data answers (scripts/build_analytics_sf.js totals.json),
 *  from the aq-health export: its field, and whether the window adds the hours up or averages them. */
const ANALYTICS_FIGURES = {
  total_delay: { field: 'delay_h', sum: true, detail: (a, b) => `${int(a)} → ${int(b)} vehicle-hours of delay` },
  co2: { field: 'vmt', sum: true, detail: (a, b) => `follows VMT: ${int(a)} → ${int(b)} vehicle miles` },
  exposure: { field: 'exposure', sum: false, detail: (a, b) => `road PM2.5 ${a.toFixed(3)} → ${b.toFixed(3)} µg/m³, resident-weighted` },
  exposure_low_income: { field: 'exposure_q1', sum: false, detail: (a, b) => `road PM2.5 ${a.toFixed(3)} → ${b.toFixed(3)} µg/m³, lowest-income fifth of tracts` },
};

/** A headline metric from the Analytics totals over the window's hours, or null. */
function analyticsFigure(workshop, period, scenario, id) {
  const spec = ANALYTICS_FIGURES[id];
  const totals = workshop.analyticsTotals;
  const own = totals && totals[scenario.id];
  const base = totals && totals.baseline;
  const hours = period && period.hours;
  if (!spec || !own || !base || !hours || !hours.length) return null;
  const over = (t) => {
    const v = hours.map((h) => t[spec.field][h]).filter((x) => x != null);
    if (!v.length) return null;
    const s = v.reduce((a, b) => a + b, 0);
    return spec.sum ? s : s / v.length;
  };
  const a = over(base);
  const b = over(own);
  if (a == null || b == null) return null;
  return { kind: 'model', value: pct(a, b), detail: spec.detail(a, b) };
}

export function figure(workshop, period, scenario, id) {
  if (scenario.id !== 'baseline') {
    const real = analyticsFigure(workshop, period, scenario, id);
    if (real) return real;
  }
  const row = CORRIDOR_ROWS.find((r) => r.id === id);
  if (row) return corridorFigure(workshop, period, scenario, row);
  const sheet = () => {
    const v = scenario.metrics[id];
    if (!v || v.value == null) return { kind: 'empty' };
    return { kind: scenario.id === 'baseline' ? 'baseline' : 'illustrative', value: v.value };
  };
  if (scenario.id === 'baseline') {
    const b = period && workshop.model?.baseline?.[period.id];
    if (!b) return sheet();
    if (id === 'inbound_auto') return { kind: 'baseline', value: 0, detail: `${int(b.entering)} vehicles` };
    if (id === 'vmt') return { kind: 'baseline', value: 0, detail: `${int(b.zoneVmtMi)} vehicle miles` };
    if (id === 'transit_share_pp' && b.driveShare != null) return { kind: 'baseline', value: 0, detail: `by car: ${share(b.driveShare)}` };
    return sheet();
  }
  const w = windowOf(workshop, scenario.id, period);
  if (!w || !w.computed) return sheet();
  if (id === 'inbound_auto') {
    return { kind: 'model', value: pct(w.entering.baseline, w.entering.scenario),
      detail: `${int(w.entering.baseline)} → ${int(w.entering.scenario)} vehicles`, title: perHour(w) };
  }
  if (id === 'vmt') {
    return { kind: 'model', value: pct(w.zoneVmtMi.baseline, w.zoneVmtMi.scenario),
      detail: `${int(w.zoneVmtMi.baseline)} → ${int(w.zoneVmtMi.scenario)} vehicle miles` };
  }
  if (id === 'transit_share_pp' && w.driveShare) {
    return { kind: 'model', value: (w.driveShare.before - w.driveShare.after) * 100,
      detail: `by car: ${share(w.driveShare.before)} → ${share(w.driveShare.after)}` };
  }
  return sheet();
}

/** Affordability: per income class, the average charge per entering vehicle where the
 *  model has it; workshop.json's illustrative index otherwise. */
export function affordability(workshop, period, scenario) {
  if (scenario.id === 'baseline' && workshop.model?.baseline?.[period?.id]) return { kind: 'baseline', text: 'No charge' };
  const w = windowOf(workshop, scenario.id, period);
  if (w && w.computed) {
    if (!w.classes) return { kind: 'model-none', text: 'Not split by income for these hours' };
    const labels = workshop.model.classLabels || {};
    const order = workshop.model.scenarios[scenario.id].classOrder || Object.keys(w.classes);
    return { kind: 'model', classes: order.map((c) => ({ id: c, name: labels[c]?.name || c, usd: w.classes[c].perEnteringVehicleUsd })) };
  }
  return figure(workshop, period, scenario, 'affordability');
}

const sameFigure = (a, b) => a.kind === 'model' && b.kind === 'model'
  && JSON.stringify({ ...a, title: null }) === JSON.stringify({ ...b, title: null });

/** The scenario whose model figure `id` this scenario's equals by construction, as
 *  workshop.json's `same_as` says, when the model's two figures are in fact equal; null
 *  otherwise. */
export function sameAs(workshop, period, scenario, id) {
  const same = scenario.same_as;
  if (!same || !(same.metrics || []).includes(id)) return null;
  const other = workshop.scenarios.find((s) => s.id === same.scenario);
  if (!other) return null;
  const pick = (s) => (id === 'affordability' ? affordability(workshop, period, s) : figure(workshop, period, s, id));
  return sameFigure(pick(scenario), pick(other)) ? other : null;
}

/** The scenario whose trips into the zone this scenario's are, by workshop.json's
 *  `same_as`, when the model shifts the same share of them in the window (the car share
 *  before and after the charge); null otherwise, and the scenario shows its own. Vehicles
 *  entering the zone are not compared: rerouted through traffic changes them. */
export function sameTripsAs(workshop, period, scenario) {
  const same = scenario.same_as;
  const other = same?.trips_into_zone ? workshop.scenarios.find((s) => s.id === same.scenario) : null;
  if (!other) return null;
  const a = windowOf(workshop, scenario.id, period);
  const b = windowOf(workshop, other.id, period);
  return a?.computed && b?.computed && a.driveShare && b.driveShare
    && a.driveShare.before === b.driveShare.before && a.driveShare.after === b.driveShare.after ? other : null;
}
